/* eslint-disable react-hooks/rules-of-hooks */
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useOutletContext } from 'react-router-dom';
import api from '@/api/axios';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { MapPinArea, Users, Warning, Flame, CurrencyDollar, ChatCircleDots, ShoppingCart } from '@phosphor-icons/react';

const VENDOR_COLORS = [
  '#1D4ED8', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#14B8A6', '#F97316', '#06B6D4', '#84CC16',
  '#A855F7', '#FACC15', '#22C55E', '#3B82F6', '#E11D48',
];

const TOPO_URL = '/maps/guatemala-deptos.json';

const Coverage = () => {
  const { user: currentUser, isAdmin } = useAuth();
  const { theme } = useTheme();
  const { selectedBusiness } = useOutletContext();
  const canView = isAdmin || currentUser?.role === 'supervisor';
  const isDark = theme === 'dark';
  // Theme-aware colors for the map
  const COLOR_UNASSIGNED = isDark ? '#1F2937' : '#E5E3DB';
  const COLOR_STROKE = isDark ? '#0F172A' : '#FFFFFF';
  const COLOR_HEAT_ZERO = isDark ? '#1F2937' : '#E5E3DB';
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [mode, setMode] = useState('coverage'); // 'coverage' | 'heatmap'
  const [heatMetric, setHeatMetric] = useState('revenue'); // 'revenue' | 'orders' | 'messages'
  const [period, setPeriod] = useState('month');
  const [scope, setScope] = useState('current'); // 'current' | 'all'
  const [deptStats, setDeptStats] = useState({});

  useEffect(() => {
    if (canView) fetchVendors();
  }, []);

  useEffect(() => {
    if (canView && mode === 'heatmap') fetchHeatmap();
  }, [mode, period, scope, selectedBusiness]);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/vendors');
      setVendors(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchHeatmap = async () => {
    try {
      const params = { period };
      if (scope === 'current' && selectedBusiness?.id) params.business_id = selectedBusiness.id;
      const r = await api.get('/reports/departments', { params });
      setDeptStats(r.data.departments || {});
    } catch (e) { console.error(e); }
  };

  // Build dept -> [vendors] map
  const deptCoverage = useMemo(() => {
    const map = {};
    vendors.forEach((v, idx) => {
      const color = VENDOR_COLORS[idx % VENDOR_COLORS.length];
      (v.departments || []).forEach(d => {
        if (!map[d]) map[d] = [];
        map[d].push({ id: v.id, name: v.name, color });
      });
    });
    return map;
  }, [vendors]);

  const vendorColorMap = useMemo(() => {
    const m = {};
    vendors.forEach((v, idx) => { m[v.id] = VENDOR_COLORS[idx % VENDOR_COLORS.length]; });
    return m;
  }, [vendors]);

  // Max value across departments for heatmap scaling (declared before early return to keep hooks order stable)
  const heatMax = useMemo(() => {
    const values = Object.values(deptStats).map(s => s?.[heatMetric] || 0);
    return Math.max(1, ...values);
  }, [deptStats, heatMetric]);

  if (!canView) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-sm text-[var(--text-muted)]">Solo administradores y supervisores pueden ver la cobertura.</p>
      </div>
    );
  }

  const getDeptFill = (deptName) => {
    if (mode === 'heatmap') {
      const stat = deptStats[deptName];
      const v = stat?.[heatMetric] || 0;
      if (v === 0) return COLOR_HEAT_ZERO;
      const intensity = Math.min(1, v / heatMax);
      // Emerald gradient based on intensity
      const r = Math.round(16 + (250 - 16) * intensity * 0.3);
      const g = Math.round(50 + (185 - 50) * intensity);
      const b = Math.round(40 + (129 - 40) * intensity * 0.5);
      return `rgb(${r}, ${g}, ${b})`;
    }
    const covering = deptCoverage[deptName] || [];
    if (selectedVendorId) {
      const isCovered = covering.some(c => c.id === selectedVendorId);
      return isCovered ? vendorColorMap[selectedVendorId] : COLOR_UNASSIGNED;
    }
    if (covering.length === 0) return COLOR_UNASSIGNED;
    if (covering.length === 1) return covering[0].color;
    return '#7C3AED';
  };

  const uncoveredCount = ['Alta Verapaz','Baja Verapaz','Chimaltenango','Chiquimula','El Progreso','Escuintla','Guatemala','Huehuetenango','Izabal','Jalapa','Jutiapa','Petén','Quetzaltenango','Quiché','Retalhuleu','Sacatepéquez','San Marcos','Santa Rosa','Sololá','Suchitepéquez','Totonicapán','Zacapa'].filter(d => !deptCoverage[d]).length;

  const sharedCount = Object.values(deptCoverage).filter(arr => arr.length > 1).length;

  return (
    <div className="min-h-screen bg-[var(--bg)] p-4 md:p-8" data-testid="coverage-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <MapPinArea size={32} weight="fill" className="text-emerald-400 drop-shadow-[0_0_12px_rgba(16,185,129,0.5)]" />
            <h1 className="text-2xl md:text-4xl font-black text-[var(--text)] tracking-tight">Cobertura Territorial</h1>
          </div>
          <p className="text-sm text-[var(--text-muted)] font-medium">Mapa de Guatemala con departamentos coloreados por vendedor asignado o por calor de actividad.</p>
        </div>

        {/* Mode toggle + filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="flex items-center gap-1 bg-[var(--surface)] border border-[var(--border)] rounded-[10px] p-1">
            <button
              onClick={() => setMode('coverage')}
              className={`text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-[8px] transition-all flex items-center gap-1.5 ${
                mode === 'coverage' ? 'bg-[#1D4ED8] text-white shadow-[0_0_10px_rgba(29,78,216,0.4)]' : 'text-[var(--text-muted)] hover:text-white'
              }`}
              data-testid="mode-coverage"
            >
              <MapPinArea size={14} weight="bold" /> Cobertura
            </button>
            <button
              onClick={() => setMode('heatmap')}
              className={`text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-[8px] transition-all flex items-center gap-1.5 ${
                mode === 'heatmap' ? 'bg-emerald-600 text-white shadow-[0_0_10px_rgba(16,185,129,0.4)]' : 'text-[var(--text-muted)] hover:text-white'
              }`}
              data-testid="mode-heatmap"
            >
              <Flame size={14} weight="bold" /> Heatmap
            </button>
          </div>

          {mode === 'heatmap' && (
            <>
              <div className="flex items-center gap-1 bg-[var(--surface)] border border-[var(--border)] rounded-[10px] p-1">
                {[
                  { k: 'revenue', label: 'Dinero', icon: CurrencyDollar },
                  { k: 'orders', label: 'Pedidos', icon: ShoppingCart },
                  { k: 'messages', label: 'Mensajes', icon: ChatCircleDots },
                ].map(m => (
                  <button
                    key={m.k}
                    onClick={() => setHeatMetric(m.k)}
                    className={`text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-[8px] transition-all flex items-center gap-1.5 ${
                      heatMetric === m.k ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'text-[var(--text-muted)] hover:text-white'
                    }`}
                    data-testid={`heat-metric-${m.k}`}
                  >
                    <m.icon size={12} weight="bold" /> {m.label}
                  </button>
                ))}
              </div>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="text-xs font-bold uppercase bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] px-3 py-2 rounded-[10px] focus:outline-none focus:border-emerald-500"
                data-testid="heat-period"
              >
                <option value="today">Hoy</option>
                <option value="week">Semana</option>
                <option value="month">Mes</option>
                <option value="year">Año</option>
              </select>
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                className="text-xs font-bold uppercase bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] px-3 py-2 rounded-[10px] focus:outline-none focus:border-emerald-500"
                data-testid="heat-scope"
              >
                <option value="current">Negocio actual</option>
                <option value="all">Todos los negocios</option>
              </select>
            </>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard label="Vendedores" value={vendors.length} color="blue" icon={Users} />
          <StatCard label="Cubiertos" value={22 - uncoveredCount} color="emerald" />
          <StatCard label="Sin cobertura" value={uncoveredCount} color="red" icon={uncoveredCount > 0 ? Warning : null} />
          <StatCard label="Compartidos" value={sharedCount} color="purple" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Map */}
          <div className="lg:col-span-2 bg-[var(--surface)] border border-[var(--border)] rounded-[16px] p-4 relative" data-testid="map-container">
            {loading ? (
              <div className="text-center text-[var(--text-muted)] py-12">Cargando mapa...</div>
            ) : (
              <ComposableMap
                projection="geoMercator"
                projectionConfig={{ center: [-90.4, 15.6], scale: 4500 }}
                width={600}
                height={520}
                style={{ width: '100%', height: 'auto' }}
              >
                <Geographies geography={TOPO_URL}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const name = geo.properties.Departamento || geo.properties.name;
                      const covering = deptCoverage[name] || [];
                      const fill = getDeptFill(name);
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={fill}
                          stroke={COLOR_STROKE}
                          strokeWidth={1}
                          onMouseEnter={() => setHovered({ name, vendors: covering })}
                          onMouseLeave={() => setHovered(null)}
                          style={{
                            default: { outline: 'none', cursor: 'pointer', transition: 'all 0.2s' },
                            hover: { fill: '#1D4ED8', outline: 'none', filter: 'brightness(1.3)' },
                            pressed: { outline: 'none' },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
              </ComposableMap>
            )}

            {/* Hover tooltip */}
            {hovered && (
              <div className="absolute top-4 left-4 bg-[var(--bg)]/95 backdrop-blur border border-white/20 rounded-[10px] p-3 shadow-xl pointer-events-none min-w-[200px]" data-testid="map-tooltip">
                <p className="text-sm font-black text-[var(--text)] mb-1">{hovered.name}</p>
                {mode === 'heatmap' ? (
                  <div className="space-y-0.5 text-xs">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[var(--text-muted)]">💰 Dinero:</span>
                      <span className="font-black text-emerald-400">Q {(deptStats[hovered.name]?.revenue || 0).toLocaleString('es-GT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[var(--text-muted)]">🛒 Pedidos:</span>
                      <span className="font-black text-[var(--text)]">{deptStats[hovered.name]?.orders || 0}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[var(--text-muted)]">💬 Mensajes:</span>
                      <span className="font-black text-[var(--text)]">{deptStats[hovered.name]?.messages || 0}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[var(--text-muted)]">📨 Chats:</span>
                      <span className="font-black text-[var(--text)]">{deptStats[hovered.name]?.conversations || 0}</span>
                    </div>
                  </div>
                ) : hovered.vendors.length === 0 ? (
                  <p className="text-xs text-red-400 font-bold">⚠ Sin vendedor asignado</p>
                ) : (
                  <div className="space-y-1">
                    {hovered.vendors.map(v => (
                      <div key={v.id} className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: v.color }} />
                        <span className="text-xs font-bold text-[var(--text)]">{v.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Legend / Vendor list / Top deptos */}
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[16px] p-4">
            {mode === 'heatmap' ? (
              <>
                <h3 className="text-sm font-black uppercase tracking-wider text-[var(--text)] mb-3 flex items-center gap-2">
                  <Flame size={16} weight="bold" className="text-emerald-400" />
                  Top Departamentos
                </h3>
                <div className="space-y-1.5 mb-3">
                  {Object.entries(deptStats)
                    .sort((a, b) => (b[1]?.[heatMetric] || 0) - (a[1]?.[heatMetric] || 0))
                    .slice(0, 10)
                    .map(([dept, stat], idx) => {
                      const v = stat?.[heatMetric] || 0;
                      const pct = heatMax > 0 ? (v / heatMax) * 100 : 0;
                      const display = heatMetric === 'revenue' ? `Q ${Math.round(v).toLocaleString('es-GT')}` : v;
                      return (
                        <div key={dept} className="space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-[var(--text)] font-bold truncate">#{idx + 1} {dept}</span>
                            <span className="text-emerald-400 font-black">{display}</span>
                          </div>
                          <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-300" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  {Object.keys(deptStats).length === 0 && (
                    <p className="text-xs text-[var(--text-faint)] px-2 py-1">Sin datos en este período.</p>
                  )}
                </div>
                <div className="pt-3 border-t border-[var(--border)]">
                  <p className="text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)] mb-2">Escala</p>
                  <div className={`h-3 rounded-full bg-gradient-to-r ${isDark ? 'from-[#1F2937]' : 'from-[#E5E3DB]'} via-emerald-700 to-emerald-300`} />
                  <div className="flex justify-between text-[10px] text-[var(--text-faint)] font-bold mt-1">
                    <span>0</span>
                    <span>{heatMax > 1000 ? `${Math.round(heatMax / 1000)}k` : Math.round(heatMax)}</span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-sm font-black uppercase tracking-wider text-[var(--text)] mb-3 flex items-center gap-2">
                  <Users size={16} weight="bold" className="text-[#1D4ED8]" />
                  Leyenda
                </h3>
                <div className="space-y-1.5 mb-4">
                  <button
                    onClick={() => setSelectedVendorId(null)}
                    className={`w-full text-left text-xs font-bold px-3 py-2 rounded-[8px] transition-all ${
                      !selectedVendorId ? 'bg-[#1D4ED8] text-white' : 'text-[var(--text)] hover:bg-white/5'
                    }`}
                    data-testid="legend-all"
                  >
                    Todos los vendedores
                  </button>
                  {vendors.filter(v => (v.departments || []).length > 0).map(v => (
                    <button
                      key={v.id}
                      onClick={() => setSelectedVendorId(selectedVendorId === v.id ? null : v.id)}
                      className={`w-full text-left text-xs font-bold px-3 py-2 rounded-[8px] transition-all flex items-center gap-2 ${
                        selectedVendorId === v.id ? 'bg-white/10 text-[var(--text)]' : 'text-[var(--text)] hover:bg-white/5'
                      }`}
                      data-testid={`legend-vendor-${v.id}`}
                    >
                      <span className="w-3 h-3 rounded-full shrink-0" style={{ background: vendorColorMap[v.id] }} />
                      <span className="truncate flex-1">{v.name}</span>
                      <span className="text-[10px] text-[var(--text-faint)]">{v.departments?.length || 0}</span>
                    </button>
                  ))}
                  {vendors.filter(v => (v.departments || []).length > 0).length === 0 && (
                    <p className="text-xs text-[var(--text-faint)] px-2 py-1">Nadie tiene departamentos asignados. Asigna desde la ficha de vendedor.</p>
                  )}
                </div>

                <div className="pt-3 border-t border-[var(--border)]">
                  <p className="text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)] mb-2">Colores especiales</p>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="w-3 h-3 rounded-full bg-[#7C3AED]" /> <span className="text-[var(--text)]">Compartido (2+ vendedores)</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="w-3 h-3 rounded-full bg-[var(--surface-muted)] border border-white/20" /> <span className="text-[var(--text)]">Sin asignar</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, color, icon: Icon }) => {
  const colors = {
    blue: 'text-[#1D4ED8] border-[#1D4ED8]/20 bg-[#1D4ED8]/5',
    emerald: 'text-emerald-400 border-emerald-500/20 bg-emerald-500/5',
    red: 'text-red-400 border-red-500/20 bg-red-500/5',
    purple: 'text-purple-400 border-purple-500/20 bg-purple-500/5',
  };
  return (
    <div className={`border rounded-[12px] p-3 ${colors[color]}`}>
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon size={14} weight="bold" />}
        <span className="text-[10px] uppercase tracking-wide font-bold opacity-80">{label}</span>
      </div>
      <p className="text-2xl font-black">{value}</p>
    </div>
  );
};

export default Coverage;
